/**
 * Wingman Event Stream
 * Handles Server-Sent Events (SSE) streaming responses from the Wingman API
 */

/**
 * Async iterable wrapper for SSE (Server-Sent Events) responses.
 * Parses the event stream format and yields parsed JSON events.
 *
 * @example
 * ```typescript
 * const stream = new WingmanEventStream<StreamingConversationMessage>(response);
 * for await (const event of stream) {
 *   console.log(event.data);
 * }
 * ```
 */
export class WingmanEventStream<T> implements AsyncIterable<{ data: T }> {
  constructor(private response: Response) {}

  async *[Symbol.asyncIterator](): AsyncIterator<{ data: T }> {
    if (!this.response.body) {
      throw new Error("Response body is null - cannot stream");
    }

    const reader = this.response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          // Process any remaining data in the buffer
          if (buffer.trim()) {
            const event = this.parseEventLine(buffer);
            if (event) {
              yield event;
            }
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // SSE format: each event is separated by double newlines
        // Each event line starts with "data: " followed by JSON
        const lines = buffer.split("\n");

        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || "";

        for (const line of lines) {
          const event = this.parseEventLine(line);
          if (event) {
            yield event;
          }
        }
      }
    } finally {
      // An iterator can end before EOF (for example, a consumer breaks).
      // Cancel the body so request resources and fallback abort listeners end too.
      try {
        await reader.cancel();
      } catch {
        // The body may already have errored after cancellation.
      }
      reader.releaseLock();
    }
  }

  /**
   * Parse a single SSE line and extract the data
   */
  private parseEventLine(line: string): { data: T } | null {
    const trimmedLine = line.trim();

    // Skip empty lines and comments
    if (!trimmedLine || trimmedLine.startsWith(":")) {
      return null;
    }

    // Parse "data: {...}" format
    if (trimmedLine.startsWith("data: ")) {
      const jsonStr = trimmedLine.slice(6).trim();

      // Skip "[DONE]" marker that some SSE implementations use
      if (!jsonStr || jsonStr === "[DONE]") {
        return null;
      }

      try {
        const data = JSON.parse(jsonStr) as T;
        return { data };
      } catch (error) {
        console.error("Failed to parse SSE event data:", jsonStr, error);
        return null;
      }
    }

    // Handle "event:" lines (currently we just skip them as we only care about data)
    if (trimmedLine.startsWith("event:")) {
      return null;
    }

    // Handle "id:" lines (for event IDs, currently unused)
    if (trimmedLine.startsWith("id:")) {
      return null;
    }

    return null;
  }
}
